import { useMutation } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  CustomerEngagementTier,
  ModuleSubscriptionStatus,
  ServiceTier,
  StaffTenantSubscription,
  StaffTenantSubscriptionStats,
  StaffTenantSubscriptionsQuery,
  StaffTenantSubscriptionsQueryVariables,
  SubscriptionOverageStatus,
  SubscriptionPresenceStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { GET_STAFF_TENANT_SUBSCRIPTIONS } from "hooks/useStaffTenantSubscriptions";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";
import { downloadFromUrl } from "util/download";

import {
  ACCOUNT_TEAM_FILTER_FIELDS,
  ACCOUNT_TEAM_HIDDEN_VISIBILITY,
  accountTeamColumnSx,
  buildAccountTeamColumns,
} from "./accountTeamColumns";

type Row = StaffTenantSubscription;

const SFDC_ACCOUNT_URL = "https://encamp.lightning.force.com/lightning/r/Account";
const SFDC_REPORT_URL =
  "https://encamp.lightning.force.com/lightning/r/Report/00ONs00000Q6wvG/view";

// Stat cards route to the grid with a pre-applied omnisearch filter on one of
// the hidden status columns. URL-driven omnisearch reads from the `omnisearch`
// query param and the backend's registered status filters translate that to a
// WHERE clause against the computed status columns.
const STAT_FILTER_OMNISEARCH = {
  chemicalOverages: "chemicalStatus:OVER",
  wasteOverages: "wasteStatus:OVER",
  chemicalWithinLimit: "chemicalStatus:WITHIN",
  chemicalReportingOverages: "chemicalReportingStatus:OVER",
  wasteReportingBiennialOverages: "wasteReportingBiennialStatus:OVER",
  wasteReportingNonBiennialOverages: "wasteReportingNonBiennialStatus:OVER",
  missingSubscription: "subscriptionStatus:MISSING",
} as const;

const REPORTING_YEAR_PARAM = "reportingYear";

// Build the stat-card click-through URL, preserving the user's currently
// selected reportingYear so a click on a year-scoped card (e.g. "Chem Rpt
// Overages (2026)") doesn't drop them back onto the resolver default year
// and lose the metric they just clicked.
function buildStatHref(omnisearch: string, reportingYearParam: string | null): string {
  const params = new URLSearchParams();
  params.set("omnisearch", omnisearch);
  if (reportingYearParam) {
    params.set(REPORTING_YEAR_PARAM, reportingYearParam);
  }
  return `?${params.toString()}`;
}

function formatSyncedAt(value: string | null | undefined): string {
  if (!value) return "Never";
  return DateTime.fromISO(value).toRelative() ?? "Never";
}

function formatSyncedAtAbsolute(value: string | null | undefined): string {
  if (!value) return "—";
  return DateTime.fromISO(value).toFormat("yyyy-LL-dd HH:mm 'UTC'");
}

function engagementLabel(tier: CustomerEngagementTier | null | undefined): string {
  if (!tier) return "—";
  if (tier === CustomerEngagementTier.SmbSmall) return "SMB/Small";
  if (tier === CustomerEngagementTier.MidMarket) return "Mid-Market";
  return "Enterprise";
}

function serviceTierLabel(tier: ServiceTier | null | undefined): string {
  if (!tier) return "—";
  return prettyPrintEnumValue(tier);
}

function moduleStatusLabel(status: ModuleSubscriptionStatus | null | undefined): string {
  if (!status) return "—";
  if (status === ModuleSubscriptionStatus.Customer) return "Active";
  return "Former";
}

type OverageDirection = "over" | "within" | "unknown";

function overageDirection(
  actual: number | null | undefined,
  contracted: number | null | undefined
): OverageDirection {
  if (actual == null || contracted == null || contracted <= 0) return "unknown";
  return actual > contracted ? "over" : "within";
}

function EngagementBadge({ tier }: { tier: CustomerEngagementTier | null | undefined }) {
  if (!tier) return <Typography variant="body2">—</Typography>;
  let color: "secondary" | "encampBlue" | "grey50";
  if (tier === CustomerEngagementTier.Enterprise) {
    color = "secondary";
  } else if (tier === CustomerEngagementTier.MidMarket) {
    color = "encampBlue";
  } else {
    color = "grey50";
  }
  return <Chip size="small" color={color} label={engagementLabel(tier)} />;
}

function ServiceTierBadge({ tier }: { tier: ServiceTier | null | undefined }) {
  if (!tier) return <Typography variant="body2">—</Typography>;
  let color: "secondary" | "encampBlue" | "grey50";
  if (tier === ServiceTier.Peak) {
    color = "secondary";
  } else if (tier === ServiceTier.Climb) {
    color = "encampBlue";
  } else {
    color = "grey50";
  }
  return <Chip size="small" color={color} label={serviceTierLabel(tier)} />;
}

function ModuleStatusBadge({ status }: { status: ModuleSubscriptionStatus | null | undefined }) {
  if (!status) return <Typography variant="body2">—</Typography>;
  return (
    <Chip
      size="small"
      color={status === ModuleSubscriptionStatus.Customer ? "encampGreen" : "encampRed"}
      label={moduleStatusLabel(status)}
    />
  );
}

function StatusBadge({ status }: { status: SubscriptionOverageStatus }) {
  if (status === SubscriptionOverageStatus.None) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Chip
      size="small"
      color={status === SubscriptionOverageStatus.Over ? "encampRed" : "encampGreen"}
      label={status === SubscriptionOverageStatus.Over ? "Over" : "Within"}
    />
  );
}

function CompareCell({
  actual,
  contracted,
}: {
  actual: number | null | undefined;
  contracted: number | null | undefined;
}) {
  const theme = useTheme();
  if (actual == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const dir = overageDirection(actual, contracted);
  const color =
    dir === "over"
      ? theme.palette.error.main
      : dir === "within"
        ? theme.palette.success.main
        : theme.palette.text.primary;
  return (
    <Typography variant="body2" sx={{ color, fontFamily: "monospace", fontWeight: 600 }}>
      {actual.toLocaleString()}
    </Typography>
  );
}

// Facility-count columns draw from two sources: SFDC-contracted numbers synced
// from Salesforce, and Encamp-actual numbers computed from Facility / report
// state. The 🏕️ prefix marks columns sourced from Encamp; unmarked columns are
// from SFDC. When source === "sfdc", pass the exact field label used in the
// SFDC report ("Customers w/ App Fields"); it is appended to the tooltip so
// staff can trace a column back to its source field.
function infoHeader(label: string, tooltip: string, source: "encamp" | "sfdc", sfdcField?: string) {
  const display = source === "encamp" ? `🏕️ ${label}` : label;
  const fullTooltip = sfdcField ? `${tooltip} SFDC field: "${sfdcField}".` : tooltip;
  // Named function so React (and react/display-name) can identify the component
  // — MUI DataGrid's renderHeader expects a component-like callable here.
  const InfoHeader = () => (
    <Tooltip title={fullTooltip} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{display}</span>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
  InfoHeader.displayName = `InfoHeader(${label})`;
  return InfoHeader;
}

function LinksCell({ row }: { row: Row }) {
  return (
    <Stack direction="row" spacing={0.5}>
      <Tooltip title="Open in Encamp Staff">
        <IconButton
          size="small"
          component={Link}
          to={`/staff/implementation/${row.tenantId}`}
          target="_blank"
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.salesforceAccountId && (
        <Tooltip title="Open in Salesforce">
          <IconButton
            size="small"
            component="a"
            href={`${SFDC_ACCOUNT_URL}/${row.salesforceAccountId}/view`}
            target="_blank"
            rel="noopener"
          >
            <OpenInNew fontSize="small" sx={{ color: "#00A1E0" }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

const EXPORT_STAFF_TENANT_SUBSCRIPTIONS_TO_CSV = gql(`
  mutation ExportStaffTenantSubscriptionsToCsv($search: String, $sort: [SortModel!], $reportingYear: Int) {
    exportStaffTenantSubscriptionsToCsv(search: $search, sort: $sort, reportingYear: $reportingYear) {
      success
      errorMsg
      downloadUrl
    }
  }
`);

function CsvExportButton({
  search,
  reportingYear,
}: {
  search: string | undefined;
  reportingYear: number | null;
}) {
  const alert = useAlerts();
  const [mutate, { data, loading }] = useMutation(EXPORT_STAFF_TENANT_SUBSCRIPTIONS_TO_CSV);
  const result = data?.exportStaffTenantSubscriptionsToCsv;

  useEffect(() => {
    if (result?.success) {
      alert.success("Successfully generated the export");
    } else if (result?.errorMsg) {
      alert.error(`There was a problem generating the export: ${result.errorMsg}`);
    }
  }, [alert, result?.errorMsg, result?.success]);

  useEffect(() => {
    if (result?.downloadUrl) {
      downloadFromUrl(result.downloadUrl);
    }
  }, [result?.downloadUrl]);

  return (
    <Tooltip title="Download all rows matching the current filter as CSV." placement="top">
      <span>
        <Button
          size="medium"
          loading={loading}
          sx={{ minWidth: 0 }}
          onClick={() => mutate({ variables: { search, sort: [], reportingYear } })}
        >
          <FileDownload />
        </Button>
      </span>
    </Tooltip>
  );
}

export const SubscriptionOverview = () => {
  useBreadcrumb({ label: "Subscription Overview" });

  const copyToClipboard = useCopyToClipboard();
  const alerts = useAlerts();
  const handleCopyClick = useCallback(
    (label: string, value: string) => async (event: React.MouseEvent) => {
      event.stopPropagation();
      await copyToClipboard(value);
      alerts.info(`${label} copied`);
    },
    [copyToClipboard, alerts]
  );
  const accountTeamColumns = useMemo(
    () => buildAccountTeamColumns((email) => handleCopyClick("Email", email)),
    [handleCopyClick]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get(REPORTING_YEAR_PARAM);
  // null = fall back to the resolver default (currentTierIIReportingYear).
  // Bounded by 2000..currentTierIIReportingYear+1 so a junk URL like
  // ?reportingYear=9999 or ?reportingYear=0000 falls back rather than
  // round-tripping through the picker as a phantom selection.
  const requestedYear = useMemo(() => {
    if (yearParam == null || !/^\d{4}$/.test(yearParam)) return null;
    const year = Number(yearParam);
    if (year < 2000 || year > currentTierIIReportingYear + 1) return null;
    return year;
  }, [yearParam]);

  const [stats, setStats] = useState<StaffTenantSubscriptionStats | undefined>(undefined);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null | undefined>(undefined);
  const [resolvedYear, setResolvedYear] = useState<number | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  // Mirror the omnisearch string into local state so the CSV export mutation
  // can pass it to the backend (which then runs the same filter + no limit).
  const [currentSearch, setCurrentSearch] = useState<string>("");

  const handleQueryStateChange = useCallback(
    ({ data, loading }: { data: StaffTenantSubscriptionsQuery | undefined; loading: boolean }) => {
      setStats(data?.staffTenantSubscriptions.stats);
      setLastSyncedAt(data?.staffTenantSubscriptions.lastSyncedAt);
      setResolvedYear(data?.staffTenantSubscriptions.reportingYear ?? null);
      setStatsLoading(loading);
    },
    []
  );

  const handleYearChange = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set(REPORTING_YEAR_PARAM, String(next));
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const queryYear = requestedYear ?? resolvedYear ?? null;

  // Every field on StaffTenantSubscriptionsQueryVariables is InputMaybe<>, so
  // an object containing only the fields we care about already satisfies the
  // full type — no Partial<> wrapper, no cast required.
  const dataQueryVariables = useMemo<StaffTenantSubscriptionsQueryVariables>(() => {
    if (requestedYear == null) return {};
    return { reportingYear: requestedYear };
  }, [requestedYear]);

  const commandButtons = useMemo(
    () => [<CsvExportButton key="csv" search={currentSearch} reportingYear={queryYear} />],
    [currentSearch, queryYear]
  );

  // Filter panel groups: separate Salesforce-sourced fields from
  // Encamp-computed fields so staff aren't comparing apples to oranges
  // when scanning the panel. Field keys must match the column `field`s
  // declared below; any column not listed lands in the default
  // "🧰 Other Filters" bucket the FilterPopover appends automatically.
  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "🧭 Account",
        description: "Pick one or more tenants to scope the table.",
        fields: ["tenantId"],
      },
      {
        name: "👥 Account Team",
        description:
          "Salesforce-synced owners of the account. Pick staff users for the Encamp roles, or search the customer POC by name or email; the grid and stat cards follow.",
        fields: ACCOUNT_TEAM_FILTER_FIELDS,
      },
      {
        name: "📇 Salesforce — Subscription",
        description:
          "Pulled from the SFDC subscription record. Reflects what we sold. Use Subscription Status to find live tenants without a subscription at all.",
        fields: [
          "subscriptionStatus",
          "customerEngagementTier",
          "chemicalServiceTier",
          "wasteServiceTier",
          "epcraSubscriptionStatus",
          "rcraSubscriptionStatus",
          "insightsSubscriptionStatus",
          "calendarSubscriptionStatus",
          "chemicalsDataPipeline",
        ],
      },
      {
        name: "📇 Salesforce — Contracted Counts",
        description: "Contracted facility counts from SFDC.",
        fields: [
          "contractedIntelligencePlatformFacilities",
          "contractedInsightsFacilities",
          "contractedChemicalSubscriptionFacilities",
          "contractedWasteSubscriptionFacilities",
          "contractedWasteReportingBiennialFacilities",
          "contractedWasteReportingNonBiennialFacilities",
        ],
      },
      {
        name: "🏕️ Encamp — Actual Counts",
        description:
          "Live facility counts computed by Encamp from current tenant + facility state.",
        fields: [
          "actualPlatformFacilities",
          "actualChemicalFacilities",
          "actualChemicalReportingFacilities",
          "actualWasteFacilities",
          "actualWasteReportingBiennialFacilities",
          "actualWasteReportingNonBiennialFacilities",
        ],
      },
      {
        name: "🏕️ Encamp — Overage Status",
        description:
          "Pre-computed flags marking each tenant as Over / Within / None for the selected year. Drives the stat-card deep links.",
        fields: [
          "chemicalStatus",
          "wasteStatus",
          "chemicalReportingStatus",
          "wasteReportingBiennialStatus",
          "wasteReportingNonBiennialStatus",
        ],
      },
    ],
    []
  );

  return (
    <Stack py={3} spacing={2} sx={accountTeamColumnSx}>
      {/* Header — intro, year picker, last sync. Title lives in the breadcrumb. */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" px={3}>
        <Typography variant="caption" color="text.secondary">
          Contracted vs actual facility counts from{" "}
          <a href={SFDC_REPORT_URL} target="_blank" rel="noopener noreferrer">
            SFDC Report 00ONs00000Q6wvG
          </a>
        </Typography>
        <TierIIYearPicker
          // Controlled mode: keep our own URL-param plumbing (reportingYear)
          // and just borrow the picker's UI for consistency with other pages.
          value={requestedYear ?? resolvedYear ?? currentTierIIReportingYear}
          onChange={(value) => {
            if (typeof value === "number") handleYearChange(value);
          }}
          selectProps={{ variant: "outlined", size: "small" }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={`Last synced: ${formatSyncedAtAbsolute(lastSyncedAt)}`} placement="top">
          <Typography variant="caption" color="text.secondary">
            Data as of {formatSyncedAt(lastSyncedAt)}
          </Typography>
        </Tooltip>
      </Stack>

      {/* Stats cards */}
      <Box px={3}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants with a SFDC TenantSubscription record (excludes demo, disabled, partner children, and orphans). Click to scope the page to this set."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name="Accounts w/ Subscription"
                  value={stats?.totalTenants}
                  loading={statsLoading}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Sum of contracted #Intelligence Platform Facilities across all subscriptions, from SFDC."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name="Contracted Platform Facilities"
                  value={stats?.totalContractedPlatformFacilities}
                  loading={statsLoading}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants where Encamp's effective chemical-subscription facility count exceeds the SFDC contracted Chem Sub count. Click to filter."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name="Chem Sub Overages"
                  value={stats?.chemicalOverages}
                  loading={statsLoading}
                  href={buildStatHref(STAT_FILTER_OMNISEARCH.chemicalOverages, yearParam)}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants where Encamp's effective waste-subscription facility count exceeds the SFDC contracted Waste Sub count. Click to filter."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name="Waste Sub Overages"
                  value={stats?.wasteOverages}
                  loading={statsLoading}
                  href={buildStatHref(STAT_FILTER_OMNISEARCH.wasteOverages, yearParam)}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants whose count of FILING_COMPLETE ANNUAL Tier II reports (data year matching the selected reporting year) exceeds their contracted Chem Rpt count. Click to filter."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name={`Chem Reporting Overages (${resolvedYear ?? "—"})`}
                  value={stats?.chemicalReportingOverages}
                  loading={statsLoading}
                  href={buildStatHref(STAT_FILTER_OMNISEARCH.chemicalReportingOverages, yearParam)}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants whose count of FILING_COMPLETE federal RCRA biennial submissions (BIENNIAL_REPORT, dueDate in the selected year) exceeds their contracted Waste Biennial count. Click to filter."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name={`Waste Biennial Overages (${resolvedYear ?? "—"})`}
                  value={stats?.wasteReportingBiennialOverages}
                  loading={statsLoading}
                  href={buildStatHref(
                    STAT_FILTER_OMNISEARCH.wasteReportingBiennialOverages,
                    yearParam
                  )}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title="Tenants whose count of FILING_COMPLETE non-biennial waste submissions (state annual / quarterly / waste notification etc., dueDate in the selected year) exceeds their contracted Waste Non-Biennial count. Click to filter."
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name={`Waste Non-Biennial Overages (${resolvedYear ?? "—"})`}
                  value={stats?.wasteReportingNonBiennialOverages}
                  loading={statsLoading}
                  href={buildStatHref(
                    STAT_FILTER_OMNISEARCH.wasteReportingNonBiennialOverages,
                    yearParam
                  )}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md>
            <Tooltip
              title={
                <Box sx={{ maxWidth: 360 }}>
                  <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                    Live tenants without a SFDC TenantSubscription
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                    Tenant exists in the app, isn&apos;t demo / disabled / a partner child, and the
                    daily Salesforce sync (report 00ONs00000Q6wvG) doesn&apos;t see it. To fix:
                    ensure the SFDC Account is included in that report and has its{" "}
                    <strong>Tenant ID</strong> column populated. Wait for the next sync (or trigger
                    one) and the count drops. Click to scope the table to these rows.
                  </Typography>
                </Box>
              }
              placement="top"
            >
              <Box sx={{ height: "100%" }}>
                <KeyMetric
                  name="Missing Subscription"
                  value={stats?.missingSubscriptionTenants}
                  loading={statsLoading}
                  href={buildStatHref(STAT_FILTER_OMNISEARCH.missingSubscription, yearParam)}
                />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Data grid */}
      <OmnisearchDataGrid<
        StaffTenantSubscriptionsQuery,
        StaffTenantSubscriptionsQueryVariables,
        Row
      >
        persistenceKey="staffTenantSubscriptions"
        isURLDriven
        onQueryStateChange={handleQueryStateChange}
        onSearchChanged={setCurrentSearch}
        getRowId={(row) => row.tenantId}
        dataQueryVariables={dataQueryVariables}
        columns={[
          {
            field: "tenantName",
            headerName: "Account",
            flex: 1.2,
            minWidth: 220,
            renderCell({ row }) {
              return (
                <Stack spacing={0.25} py={0.5} alignItems="flex-start">
                  <Tooltip title={`Click to copy: ${row.tenantName}`} placement="top">
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      onClick={handleCopyClick("Tenant name", row.tenantName)}
                      sx={{ cursor: "pointer" }}
                    >
                      {row.tenantName}
                      {row.isPartner && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          (<em>Partner</em>)
                        </Typography>
                      )}
                    </Typography>
                  </Tooltip>
                  {(row.isDemo || row.isDisabled) && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {row.isDemo && <Chip size="small" label="Demo" color="grey50" />}
                      {row.isDisabled && <Chip size="small" label="Disabled" color="encampRed" />}
                    </Stack>
                  )}
                </Stack>
              );
            },
          },
          {
            field: "tenantId",
            headerName: "Tenant ID",
            flex: 0.9,
            minWidth: 280,
            sortable: false,
            renderCell({ row }) {
              return (
                <Tooltip title={`Click to copy: ${row.tenantId}`} placement="top">
                  <Typography
                    variant="body2"
                    onClick={handleCopyClick("Tenant ID", row.tenantId)}
                    sx={{ cursor: "pointer", fontFamily: "monospace", fontSize: "0.78rem" }}
                  >
                    {row.tenantId}
                  </Typography>
                </Tooltip>
              );
            },
          },
          {
            field: "customerEngagementTier",
            headerName: "Engagement",
            renderHeader: infoHeader(
              "Engagement",
              "Customer engagement tier.",
              "sfdc",
              "Customer Engagement Tier"
            ),
            flex: 0.6,
            minWidth: 120,
            filterKeyType: "enum",
            enumValues: Object.values(CustomerEngagementTier),
            renderCell({ row }) {
              if (row.subscriptionStatus === SubscriptionPresenceStatus.Missing) {
                return (
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 360 }}>
                        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                          No SFDC TenantSubscription record
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 0.5, lineHeight: 1.4 }}
                        >
                          The Salesforce sync only creates a TenantSubscription when the tenant
                          appears in SFDC report 00ONs00000Q6wvG ("Customers w/ App Fields
                          (Subscription Sync)") with the <strong>Tenant ID</strong> column
                          populated.
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 0.75, lineHeight: 1.4 }}
                        >
                          To fix:
                          <br />
                          1. Open that report and search for{" "}
                          <code style={{ fontSize: "0.85em" }}>{row.tenantId}</code>.
                          <br />
                          2. If absent, the SFDC Account either isn&apos;t included by the
                          report&apos;s filter or has no Tenant ID — fix the Account in SFDC.
                          <br />
                          3. Wait for the next daily sync (or trigger a SALESFORCE_TENANT_SYNC job
                          manually). The chip will clear once the row appears.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                  >
                    <Chip size="small" label="No Subscription" color="encampRed" />
                  </Tooltip>
                );
              }
              return <EngagementBadge tier={row.customerEngagementTier} />;
            },
          },
          ...accountTeamColumns,
          {
            field: "chemicalServiceTier",
            headerName: "Chem Tier",
            renderHeader: infoHeader(
              "Chem Tier",
              "Chemical service tier.",
              "sfdc",
              "Chemical Service Tier"
            ),
            flex: 0.5,
            minWidth: 100,
            filterKeyType: "enum",
            enumValues: Object.values(ServiceTier),
            renderCell({ row }) {
              return <ServiceTierBadge tier={row.chemicalServiceTier} />;
            },
          },
          {
            field: "wasteServiceTier",
            headerName: "Waste Tier",
            renderHeader: infoHeader(
              "Waste Tier",
              "Waste service tier.",
              "sfdc",
              "Waste Service Tier"
            ),
            flex: 0.5,
            minWidth: 100,
            filterKeyType: "enum",
            enumValues: Object.values(ServiceTier),
            renderCell({ row }) {
              return <ServiceTierBadge tier={row.wasteServiceTier} />;
            },
          },
          {
            field: "contractedIntelligencePlatformFacilities",
            headerName: "Platform",
            renderHeader: infoHeader(
              "Platform",
              "Contracted count of facilities on the Encamp platform.",
              "sfdc",
              "# Intelligence Platform Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualPlatformFacilities",
            headerName: "Platform Actual",
            renderHeader: infoHeader(
              "Platform Actual",
              "Active facilities on the Encamp platform right now (status ≠ CLOSED). From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 130,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedInsightsFacilities",
            headerName: "Insights Sub",
            renderHeader: infoHeader(
              "Insights Sub",
              "Contracted count of facilities on the Insights subscription.",
              "sfdc",
              "# Insights Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 110,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedChemicalSubscriptionFacilities",
            headerName: "Chem Sub",
            renderHeader: infoHeader(
              "Chem Sub",
              "Contracted count of facilities with a chemical subscription.",
              "sfdc",
              "# Chemical Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualChemicalFacilities",
            headerName: "Chem Sub Actual",
            renderHeader: infoHeader(
              "Chem Sub Actual",
              "Active facilities with an effective chemical subscription: per-facility override ENABLED, or INHERIT combined with the org-level chemical module flag. Compared against Chem Sub. From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 115,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualChemicalFacilities}
                  contracted={row.contractedChemicalSubscriptionFacilities}
                />
              );
            },
          },
          {
            field: "contractedChemicalReportingFacilities",
            headerName: "Chem Rpt",
            renderHeader: infoHeader(
              "Chem Rpt",
              "Contracted count of facilities included in chemical reporting.",
              "sfdc",
              "# Chemical Reporting Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualChemicalReportingFacilities",
            headerName: "Chem Rpt Filed",
            renderHeader: infoHeader(
              `Chem Rpt Filed${resolvedYear ? ` (${resolvedYear})` : ""}`,
              "Distinct facilities with an ANNUAL Tier II report in encampStatus = FILING_COMPLETE whose TierIIReport.reportingYear matches the selected year (data year — typically filed early in year + 1). In-flight statuses (AWAITING_VERIFICATION, IN_PROGRESS, etc.) are excluded. Off-cadence (CA / NJ etc.) and non-annual report kinds are excluded. Compared against Chem Rpt. From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 130,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualChemicalReportingFacilities}
                  contracted={row.contractedChemicalReportingFacilities}
                />
              );
            },
          },
          {
            field: "contractedWasteSubscriptionFacilities",
            headerName: "Waste Sub",
            renderHeader: infoHeader(
              "Waste Sub",
              "Contracted count of facilities with a waste subscription.",
              "sfdc",
              "# Waste Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualWasteFacilities",
            headerName: "Waste Sub Actual",
            renderHeader: infoHeader(
              "Waste Sub Actual",
              "Active facilities with an effective waste subscription: per-facility override ENABLED, or INHERIT combined with the org-level waste module flag. Compared against Waste Sub. From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 115,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualWasteFacilities}
                  contracted={row.contractedWasteSubscriptionFacilities}
                />
              );
            },
          },
          {
            field: "contractedWasteReportingBiennialFacilities",
            headerName: "Waste Biennial",
            renderHeader: infoHeader(
              "Waste Biennial",
              "Contracted count of facilities included in biennial waste reporting.",
              "sfdc",
              "# Waste Reporting Facilities - Biennial"
            ),
            flex: 0.5,
            minWidth: 125,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualWasteReportingBiennialFacilities",
            headerName: "Waste Bi. Filed",
            renderHeader: infoHeader(
              `Waste Bi. Filed${resolvedYear ? ` (${resolvedYear})` : ""}`,
              "Distinct FILING_COMPLETE federal RCRA biennial Submissions (BIENNIAL_REPORT) whose Submission.dueDate falls in the selected year (e.g. the 2025 biennium lands on the 2026 row, when it's due). Counts real Submissions, not virtual events — may differ from /staff/fulfillment/waste, which expands recurring Tasks into per-occurrence rows. Compared against Waste Biennial (contracted facilities). From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 140,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualWasteReportingBiennialFacilities}
                  contracted={row.contractedWasteReportingBiennialFacilities}
                />
              );
            },
          },
          {
            field: "contractedWasteReportingNonBiennialFacilities",
            headerName: "Waste Non-Biennial",
            renderHeader: infoHeader(
              "Waste Non-Biennial",
              "Contracted count of facilities included in non-biennial waste reporting.",
              "sfdc",
              "# Waste Reporting Fac - non-Biennial"
            ),
            flex: 0.5,
            minWidth: 150,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualWasteReportingNonBiennialFacilities",
            headerName: "Waste Non-Bi. Filed",
            renderHeader: infoHeader(
              `Waste Non-Bi. Filed${resolvedYear ? ` (${resolvedYear})` : ""}`,
              "Distinct FILING_COMPLETE non-biennial waste Submissions (state annual / quarterly / waste notification etc.) whose Submission.dueDate falls in the selected year. Counts real Submissions, not virtual events — a facility filing AZ_REGISTRATION_REPORT + AZ_GENERATION_REPORT counts each as a separate report; a multi-facility submission counts once; recurring-Task expansion is NOT applied. May differ from /staff/fulfillment/waste, which expands recurring Tasks into per-occurrence rows. Compared against Waste Non-Biennial (contracted facilities). From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 160,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualWasteReportingNonBiennialFacilities}
                  contracted={row.contractedWasteReportingNonBiennialFacilities}
                />
              );
            },
          },
          {
            field: "epcraSubscriptionStatus",
            headerName: "EPCRA",
            renderHeader: infoHeader(
              "EPCRA",
              "EPCRA (Tier II) module subscription status.",
              "sfdc",
              "EPCRA Subscription Status"
            ),
            flex: 0.4,
            minWidth: 95,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.epcraSubscriptionStatus} />;
            },
          },
          {
            field: "rcraSubscriptionStatus",
            headerName: "RCRA",
            renderHeader: infoHeader(
              "RCRA",
              "RCRA (waste) module subscription status.",
              "sfdc",
              "RCRA Subscription Status"
            ),
            flex: 0.4,
            minWidth: 95,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.rcraSubscriptionStatus} />;
            },
          },
          {
            field: "insightsSubscriptionStatus",
            headerName: "Insights",
            renderHeader: infoHeader(
              "Insights",
              "Insights module subscription status.",
              "sfdc",
              "Insights Subscription Status"
            ),
            flex: 0.5,
            minWidth: 110,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.insightsSubscriptionStatus} />;
            },
          },
          {
            field: "calendarSubscriptionStatus",
            headerName: "Calendar",
            renderHeader: infoHeader(
              "Calendar",
              "Calendar module subscription status.",
              "sfdc",
              "Calendar Subscription Status"
            ),
            flex: 0.5,
            minWidth: 110,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.calendarSubscriptionStatus} />;
            },
          },
          {
            field: "chemicalsDataPipeline",
            headerName: "Chem Pipeline",
            renderHeader: infoHeader(
              "Chem Pipeline",
              "Whether the tenant has the chemicals data pipeline enabled.",
              "sfdc",
              "Chemicals Data Pipeline?"
            ),
            flex: 0.4,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return row.chemicalsDataPipeline ? (
                <Chip size="small" label="Yes" color="encampGreen" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              );
            },
          },
          {
            field: "links",
            headerName: "",
            flex: 0.3,
            minWidth: 75,
            sortable: false,
            renderCell({ row }) {
              return <LinksCell row={row} />;
            },
          },
          // Hidden-by-default columns that power the overage filters. Filter
          // chips and the stat-card deep links route through these fields.
          {
            field: "chemicalStatus",
            headerName: "Chem Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.chemicalStatus} />;
            },
          },
          {
            field: "wasteStatus",
            headerName: "Waste Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.wasteStatus} />;
            },
          },
          {
            field: "chemicalReportingStatus",
            headerName: "Chem Rpt Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 130,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.chemicalReportingStatus} />;
            },
          },
          {
            field: "wasteReportingBiennialStatus",
            headerName: "Waste Biennial Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 160,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.wasteReportingBiennialStatus} />;
            },
          },
          {
            field: "wasteReportingNonBiennialStatus",
            headerName: "Waste Non-Biennial Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 180,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.wasteReportingNonBiennialStatus} />;
            },
          },
          {
            field: "subscriptionStatus",
            headerName: "Subscription Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionPresenceStatus),
            flex: 0.5,
            minWidth: 160,
            sortable: false,
            renderCell({ row }) {
              return (
                <Chip
                  size="small"
                  color={
                    row.subscriptionStatus === SubscriptionPresenceStatus.Present
                      ? "encampGreen"
                      : "encampRed"
                  }
                  label={
                    row.subscriptionStatus === SubscriptionPresenceStatus.Present
                      ? "Present"
                      : "Missing"
                  }
                />
              );
            },
          },
        ]}
        initialColumnVisibilityModel={{
          tenantId: false,
          ...ACCOUNT_TEAM_HIDDEN_VISIBILITY,
          contractedChemicalReportingFacilities: false,
          chemicalStatus: false,
          wasteStatus: false,
          chemicalReportingStatus: false,
          wasteReportingBiennialStatus: false,
          wasteReportingNonBiennialStatus: false,
          subscriptionStatus: false,
        }}
        dataQuery={GET_STAFF_TENANT_SUBSCRIPTIONS}
        getItems={(data) => data.staffTenantSubscriptions.items}
        getCount={(data) => data.staffTenantSubscriptions.count}
        excludeFilterColumns={[
          "links",
          "contractedChemicalReportingFacilities",
          // Hidden from the filter panel because the same intent is served by
          // the dropdown picker added via additionalFilterColumns below;
          // free-text search still works through the omnisearch search bar.
          "tenantName",
          // Hidden-by-default tenantId column is just for copy-to-clipboard;
          // the Account picker already provides ID-based filtering.
          "tenantId",
        ]}
        additionalFilterColumns={[
          {
            header: "Account",
            key: "tenantId",
            filterKeyType: "multipleTenant",
          },
        ]}
        filterGroups={filterGroups}
        wideLayout
        commandButtons={commandButtons}
      />
    </Stack>
  );
};
